import { s } from '@/theme/tokens';
import { DeltaWithIndicator } from '@/ui/composites/deltaIndicator';
import { Box, Surface, Text } from '@/ui/primitives';
import { Chip } from '../primitives/Chip';
import { getDeltaTextColor } from './table/deltaColors';
import type { SxProps, Theme } from '@mui/material/styles';
import type { ReactElement, ReactNode } from 'react';

type MetricCardProps = {
  status: ReactNode;
  statusTone?: 'success' | 'warning' | 'error' | 'info' | 'brown' | 'primary' | 'default';
  count: ReactNode;
  percentage?: ReactNode;
  delta?: ReactNode;
  deltaTone?: 'positive' | 'negative' | 'neutral';
  countDelta?: ReactNode;
  countDeltaTone?: 'positive' | 'negative' | 'neutral';
  percentageDelta?: ReactNode;
  percentageDeltaTone?: 'positive' | 'negative' | 'neutral';
  surfaceSx?: SxProps<Theme>;
  percentageTextSx?: SxProps<Theme>;
  deltaTextSx?: SxProps<Theme>;
};

const resolveDeltaColor = (muiTheme: Theme, tone: MetricCardProps['deltaTone']): string => {
  if (tone === 'positive') return getDeltaTextColor(muiTheme, true);
  if (tone === 'negative') return getDeltaTextColor(muiTheme, false);
  return muiTheme.palette.text.secondary;
};

const resolveDeltaIndicatorFromValue = (
  delta: string | number,
): { symbol: string; visible: boolean } => {
  if (typeof delta === 'number') {
    if (delta > 0) return { symbol: '▲', visible: true };
    if (delta < 0) return { symbol: '▼', visible: true };
    return { symbol: '', visible: false };
  }
  const trimmed = delta.trim();
  if (trimmed.startsWith('+')) return { symbol: '▲', visible: true };
  if (trimmed.startsWith('-')) return { symbol: '▼', visible: true };
  return { symbol: '', visible: false };
};

const resolveDeltaValue = (delta?: ReactNode): ReactNode => {
  if (delta === 0 || delta === '0' || delta === '0%') {
    return '-';
  }
  if (delta === '-') return '-';
  if (typeof delta === 'string' || typeof delta === 'number') {
    const indicator = resolveDeltaIndicatorFromValue(delta);
    if (!indicator.visible) return delta;
    return <DeltaWithIndicator label={delta} isPositive={indicator.symbol === '▲'} />;
  }
  return delta;
};

type ResolvedSxValue = Exclude<SxProps<Theme>, readonly unknown[]>;

const mergeSx = (...sxValues: Array<SxProps<Theme> | undefined>): SxProps<Theme> => {
  const resolved: ResolvedSxValue[] = [];
  sxValues.forEach((sxValue) => {
    if (sxValue == null) return;
    if (Array.isArray(sxValue)) {
      resolved.push(
        ...(sxValue.filter(
          (item): item is ResolvedSxValue => item !== null && item !== undefined && item !== false,
        ) as ResolvedSxValue[]),
      );
      return;
    }
    resolved.push(sxValue as ResolvedSxValue);
  });
  return resolved as SxProps<Theme>;
};

export const MetricCard = ({
  status,
  statusTone = 'default',
  count,
  percentage,
  delta,
  deltaTone = 'neutral',
  countDelta,
  countDeltaTone,
  percentageDelta,
  percentageDeltaTone,
  surfaceSx,
  percentageTextSx,
  deltaTextSx,
}: MetricCardProps): ReactElement => {
  const isTotal = typeof status === 'string' && status.trim().toLowerCase() === 'total';
  const showPercentage =
    !isTotal && percentage !== null && percentage !== undefined && percentage !== '';
  const useSplitDeltas = countDelta != null || percentageDelta != null;
  const resolvedCountDelta = useSplitDeltas ? countDelta : undefined;
  const resolvedCountDeltaTone = countDeltaTone ?? deltaTone;
  const showCountDelta = resolvedCountDelta !== null && resolvedCountDelta !== undefined;
  const showPercentageDelta =
    useSplitDeltas && !isTotal && percentageDelta !== null && percentageDelta !== undefined;
  const showLegacyDelta = !useSplitDeltas && delta !== null && delta !== undefined;
  const gridColumns = isTotal
    ? showCountDelta
      ? '1fr 1fr 1fr'
      : '1fr 1fr'
    : showCountDelta || showPercentageDelta
      ? '1fr 1fr 1fr 1fr 1fr'
      : showLegacyDelta
        ? '1fr 1fr 1fr 1fr'
        : '1fr 1fr 1fr';
  const mergedSurfaceSx: SxProps<Theme> = surfaceSx
    ? ([
        { backgroundColor: 'background.paper' },
        ...(Array.isArray(surfaceSx) ? surfaceSx : [surfaceSx]),
      ] as SxProps<Theme>)
    : ({ backgroundColor: 'background.paper' } as SxProps<Theme>);

  if (useSplitDeltas) {
    return (
      <Surface padding="lg" sx={mergedSurfaceSx}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            gridTemplateRows: 'auto auto',
            columnGap: s('md'),
            rowGap: s('xs'),
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              gridColumn: '1',
              gridRow: '1',
              display: 'flex',
              justifyContent: 'flex-start',
              alignSelf: 'center',
            }}
          >
            <Chip label={status} tone={statusTone} transform="uppercase" />
          </Box>
          <Box
            sx={{
              gridColumn: '2',
              gridRow: '1',
              display: 'flex',
              alignItems: 'baseline',
              gap: s('sm'),
              flexWrap: 'wrap',
              justifyContent: 'flex-start',
            }}
          >
            <Text role="sectionTitle">{count}</Text>
            {showCountDelta ? (
              <Text
                role="bodyMuted"
                sx={mergeSx(
                  (muiTheme: Theme) => ({
                    color: resolveDeltaColor(muiTheme, resolvedCountDeltaTone),
                    fontSize: muiTheme.typography.body.fontSize,
                  }),
                  deltaTextSx,
                )}
              >
                {resolveDeltaValue(resolvedCountDelta)}
              </Text>
            ) : null}
          </Box>
          {!isTotal && (showPercentage || showPercentageDelta) ? (
            <Box
              sx={{
                gridColumn: '2',
                gridRow: '2',
                display: 'flex',
                alignItems: 'baseline',
                gap: s('sm'),
                flexWrap: 'wrap',
                justifyContent: 'flex-start',
              }}
            >
              {showPercentage ? (
                <Text role="bodyMuted" sx={percentageTextSx}>
                  {percentage}
                </Text>
              ) : null}
              {showPercentageDelta ? (
                <Text
                  role="bodyMuted"
                  sx={mergeSx(
                    (muiTheme: Theme) => ({
                      color: resolveDeltaColor(muiTheme, percentageDeltaTone ?? 'neutral'),
                      fontSize: muiTheme.typography.body.fontSize,
                    }),
                    deltaTextSx,
                  )}
                >
                  {resolveDeltaValue(percentageDelta)}
                </Text>
              ) : null}
            </Box>
          ) : null}
        </Box>
      </Surface>
    );
  }

  return (
    <Surface padding="lg" sx={mergedSurfaceSx}>
      <Box
        sx={{
          display: { xs: 'grid', sm: 'flex' },
          gridTemplateColumns: {
            xs: gridColumns,
            sm: 'none',
          },
          alignItems: 'center',
          columnGap: { xs: s('sm'), sm: s('md') },
          rowGap: { xs: s('xs'), sm: s('none') },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', sm: 'flex-start' } }}>
          <Chip label={status} tone={statusTone} transform="uppercase" />
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: {
              xs: isTotal && !showCountDelta ? 'flex-end' : 'center',
              sm: 'flex-start',
            },
            textAlign: {
              xs: isTotal && !showCountDelta ? 'right' : 'center',
              sm: 'left',
            },
          }}
        >
          <Text role="sectionTitle">{count}</Text>
        </Box>
        {showCountDelta ? (
          <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-end', sm: 'flex-start' } }}>
            <Text
              role="bodyMuted"
              sx={mergeSx(
                (muiTheme: Theme) => ({
                  color: resolveDeltaColor(muiTheme, resolvedCountDeltaTone),
                  fontSize: muiTheme.typography.body.fontSize,
                }),
                deltaTextSx,
              )}
            >
              {resolveDeltaValue(resolvedCountDelta)}
            </Text>
          </Box>
        ) : null}
        {isTotal ? null : (
          <Box
            sx={{
              display: 'flex',
              justifyContent: {
                xs: !(showPercentageDelta || showLegacyDelta) ? 'flex-end' : 'center',
                sm: 'flex-start',
              },
              textAlign: {
                xs: !(showPercentageDelta || showLegacyDelta) ? 'right' : 'center',
                sm: 'left',
              },
            }}
          >
            {showPercentage ? (
              <Text role="bodyMuted" sx={percentageTextSx}>
                {percentage}
              </Text>
            ) : null}
          </Box>
        )}
        {showLegacyDelta ? (
          <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-end', sm: 'flex-start' } }}>
            <Text
              role="bodyMuted"
              sx={mergeSx(
                (muiTheme: Theme) => ({
                  color: resolveDeltaColor(muiTheme, deltaTone),
                  fontSize: muiTheme.typography.body.fontSize,
                }),
                deltaTextSx,
              )}
            >
              {resolveDeltaValue(delta)}
            </Text>
          </Box>
        ) : null}
        {showPercentageDelta ? (
          <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-end', sm: 'flex-start' } }}>
            <Text
              role="bodyMuted"
              sx={mergeSx(
                (muiTheme: Theme) => ({
                  color: resolveDeltaColor(muiTheme, percentageDeltaTone ?? 'neutral'),
                  fontSize: muiTheme.typography.body.fontSize,
                }),
                deltaTextSx,
              )}
            >
              {resolveDeltaValue(percentageDelta)}
            </Text>
          </Box>
        ) : null}
      </Box>
    </Surface>
  );
};
